import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, User, CheckCircle2, Smile, Star } from 'lucide-react';
import { UserProfile } from '../../types';
import { collection, query, orderBy, onSnapshot, addDoc, doc, updateDoc, setDoc, getDoc, limitToLast } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { motion } from 'motion/react';
import { FadeInImage } from './FadeInImage';
import { useBackButton } from '../../hooks/useBackButton';

interface Props {
  currentUser: UserProfile;
  recipientId: string;
  recipientName: string;
  recipientPhotoUrl?: string;
  recipientPhone?: string;
  onClose: () => void;
  isFriend?: boolean;
  isSupport?: boolean;
  isSuspended?: boolean;
}

interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  createdAt: number;
  reaction?: string;
  isOptimistic?: boolean;
}

const formatMessageDateTime = (timestamp: number) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  if (msgDate === today) {
    return `Hoy, ${timeStr}`;
  } else if (msgDate === yesterday) {
    return `Ayer, ${timeStr}`;
  } else {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year} - ${timeStr}`;
  }
};

export function PrivateChatModal({ currentUser, recipientId, recipientName, recipientPhotoUrl, recipientPhone, onClose, isFriend, isSupport = false, isSuspended = false }: Props) {
  useBackButton(true, onClose);
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [optimisticMessages, setOptimisticMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeReactionMsgId, setActiveReactionMsgId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const allMessages = [
    ...messages,
    ...optimisticMessages.filter(optMsg => 
      !messages.some(m => 
        m.senderId === optMsg.senderId && 
        m.text === optMsg.text && 
        Math.abs(m.createdAt - optMsg.createdAt) < 10000
      )
    )
  ].sort((a, b) => a.createdAt - b.createdAt);

  const [limitCount, setLimitCount] = useState(15);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialRef = useRef(true);
  const isPaginatingRef = useRef(false);
  const scrollHeightRef = useRef(0);
  const prevFirstIdRef = useRef<string | undefined>(undefined);
  const prevMessagesLengthRef = useRef(0);

  const [mounted, setMounted] = useState(false);
  const [viewportStyle, setViewportStyle] = useState<React.CSSProperties>({});
  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({});
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalLeft = document.body.style.left;
    const originalWidth = document.body.style.width;

    const scrollY = window.scrollY;

    // Freeze screen background on mobile to prevent scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.width = '100%';

    const updateViewport = () => {
      const vv = window.visualViewport;
      if (vv) {
        setViewportStyle({
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: `${vv.height}px`,
          zIndex: 200,
        });
        const scale = vv.scale || 1;
        setCardStyle({
          transform: `scale(${1 / scale})`,
          transformOrigin: 'center center',
        });
      }
    };

    updateViewport();

    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);

    // Globally prevent native pinch-to-zoom gestures in capture phase to completely lock zoom while modal is open
    const preventZoomCapture = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };

    // Stop native touch event propagation for single-finger touches to prevent triggering global index.html virtual zoom
    const handleTouchStartMove = (e: TouchEvent) => {
      e.stopPropagation();
    };

    const handleTouchEndCancel = (e: TouchEvent) => {
      e.stopPropagation();
    };

    // Register capture-phase listeners on window to guarantee blocking native zoom before any stopPropagation
    window.addEventListener('touchstart', preventZoomCapture, { capture: true, passive: false });
    window.addEventListener('touchmove', preventZoomCapture, { capture: true, passive: false });

    const el = modalRef.current;
    if (el) {
      el.addEventListener('touchstart', handleTouchStartMove, { passive: true });
      el.addEventListener('touchmove', handleTouchStartMove, { passive: true });
      el.addEventListener('touchend', handleTouchEndCancel, { passive: true });
      el.addEventListener('touchcancel', handleTouchEndCancel, { passive: true });
    }

    return () => {
      // Restore original body styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.width = originalWidth;

      // Restore physical scroll position
      window.scrollTo(0, scrollY);

      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('touchstart', preventZoomCapture, { capture: true });
      window.removeEventListener('touchmove', preventZoomCapture, { capture: true });
      if (el) {
        el.removeEventListener('touchstart', handleTouchStartMove);
        el.removeEventListener('touchmove', handleTouchStartMove);
        el.removeEventListener('touchend', handleTouchEndCancel);
        el.removeEventListener('touchcancel', handleTouchEndCancel);
      }
    };
  }, [mounted]);

  const REACTIONS = ['👍', '❤️', '😂', '🔥', '👏', '😥'];

  // Generate a consistent chat ID based on the two UIDs
  const chatId = [currentUser.uid, recipientId].sort().join('_');

  // Reset when chatId changes
  useEffect(() => {
    isInitialRef.current = true;
    setLimitCount(15);
    setHasMore(true);
    setOptimisticMessages([]);
    prevFirstIdRef.current = undefined;
    prevMessagesLengthRef.current = 0;
  }, [chatId]);

  const handleReaction = async (msg: ChatMessage, emoji: string) => {
    const previousReaction = msg.reaction;
    const targetEmoji = previousReaction === emoji ? null : emoji;

    // Apply optimistic update immediately
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reaction: targetEmoji || undefined } : m));
    setActiveReactionMsgId(null);

    try {
      const msgRef = doc(db, getCollectionPath('private_chats'), chatId, 'messages', msg.id);
      if (previousReaction === emoji) {
        await updateDoc(msgRef, { reaction: null });
      } else {
        await updateDoc(msgRef, { reaction: emoji });
      }
    } catch (err) {
      console.error("Error setting reaction:", err);
      // Revert optimistic update if Firestore failed
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reaction: previousReaction } : m));
    }
  };

  useEffect(() => {
    const q = query(
      collection(db, getCollectionPath('private_chats'), chatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limitToLast(limitCount)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: ChatMessage[] = [];
      snapshot.forEach(doc => {
        msgs.push({ id: doc.id, ...doc.data() } as ChatMessage);
      });

      if (msgs.length < limitCount) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [chatId, limitCount]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (container.scrollTop <= 5 && hasMore && !isPaginatingRef.current) {
      scrollHeightRef.current = container.scrollHeight;
      isPaginatingRef.current = true;
      setLimitCount(prev => prev + 15);
    }
  };

  useEffect(() => {
    const currentFirstId = allMessages[0]?.id;
    const prevFirstId = prevFirstIdRef.current;
    
    if (isInitialRef.current && allMessages.length > 0) {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
      isInitialRef.current = false;
    } else if (isPaginatingRef.current && containerRef.current) {
      const container = containerRef.current;
      const addedHeight = container.scrollHeight - scrollHeightRef.current;
      container.scrollTop = addedHeight;
      isPaginatingRef.current = false;
    } else if (prevFirstId && currentFirstId === prevFirstId && allMessages.length > prevMessagesLengthRef.current) {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }

    prevFirstIdRef.current = currentFirstId;
    prevMessagesLengthRef.current = allMessages.length;
  }, [allMessages]);

  useEffect(() => {
    // Mark as read when opening or receiving new messages
    const markAsRead = async () => {
      try {
        const chatRef = doc(db, getCollectionPath('private_chats'), chatId);
        const chatSnap = await getDoc(chatRef);
        if (chatSnap.exists()) {
          const data = chatSnap.data();
          if (data.unreadCount && data.unreadCount[currentUser.uid] > 0) {
            await updateDoc(chatRef, {
              [`unreadCount.${currentUser.uid}`]: 0
            });
          }
        }
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    };

    markAsRead();
  }, [chatId, currentUser.uid, messages.length]); // Re-run when messages change

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newMessage.trim()) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    const optimisticId = `opt-${Date.now()}`;
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUser.uid,
      text: messageText,
      createdAt: Date.now(),
      isOptimistic: true
    };

    setOptimisticMessages(prev => [...prev, optimisticMsg]);

    // Execute in the background asynchronously to prevent blocking the UI
    (async () => {
      try {
        const chatRef = doc(db, getCollectionPath('private_chats'), chatId);
        const chatSnap = await getDoc(chatRef);

        if (!chatSnap.exists()) {
          await setDoc(chatRef, {
            participantIds: [currentUser.uid, recipientId],
            participantNames: {
              [currentUser.uid]: currentUser.name,
              [recipientId]: recipientName
            },
            participantPhotos: {
              [currentUser.uid]: currentUser.photoUrl || '',
              [recipientId]: recipientPhotoUrl || ''
            },
            updatedAt: Date.now(),
            lastMessage: messageText,
            lastMessageSenderId: currentUser.uid,
            unreadCount: {
              [currentUser.uid]: 0,
              [recipientId]: 1
            }
          });
        } else {
          const currentUnread = chatSnap.data().unreadCount?.[recipientId] || 0;
          await updateDoc(chatRef, {
            updatedAt: Date.now(),
            lastMessage: messageText,
            lastMessageSenderId: currentUser.uid,
            [`unreadCount.${recipientId}`]: currentUnread + 1,
            [`participantNames.${currentUser.uid}`]: currentUser.name,
            [`participantPhotos.${currentUser.uid}`]: currentUser.photoUrl || '',
            [`participantNames.${recipientId}`]: recipientName,
            [`participantPhotos.${recipientId}`]: recipientPhotoUrl || ''
          });
        }

        await addDoc(collection(db, getCollectionPath('private_chats'), chatId, 'messages'), {
          senderId: currentUser.uid,
          text: messageText,
          createdAt: optimisticMsg.createdAt
        });

        // Clean up optimistic message once the snapshot updates
        setOptimisticMessages(prev => prev.filter(m => m.id !== optimisticId));
      } catch (error) {
        console.error("Error sending private message:", error);
        setOptimisticMessages(prev => prev.filter(m => m.id !== optimisticId));
        // Restore input so they don't lose what they typed
        setNewMessage(messageText);
      }
    })();
  };

  if (!mounted) return null;

  return createPortal(
    <div ref={modalRef} className="z-[200] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 md:p-6 animate-in fade-in" style={viewportStyle} onClick={onClose}>
      <div style={cardStyle} className="w-full max-w-lg h-[600px] max-h-[90%] flex flex-col pointer-events-auto">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="w-full h-full theme-bg-card border border-white/10 rounded-[2rem] overflow-hidden flex flex-col shadow-2xl text-white" 
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/5 flex items-center justify-between bg-black/20">
            <div className="flex items-center gap-3">
              {isSupport ? (
                <div className="w-10 h-10 rounded-full bg-theme-primary/10 flex items-center justify-center border border-theme-primary/30 shrink-0 animate-pulse">
                  <User size={20} className="theme-primary-text" />
                </div>
              ) : recipientPhotoUrl ? (
                <FadeInImage src={recipientPhotoUrl} alt={recipientName} className="w-10 h-10 rounded-full object-cover border border-theme-primary/30 shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full theme-primary-bg/20 flex items-center justify-center border border-theme-primary/30">
                  <User size={20} className="theme-primary-text" />
                </div>
              )}
              <div>
                <h3 className="font-bold theme-font-secondary text-lg flex items-center gap-1.5">
                  <span>{isSupport ? 'Chat de Soporte' : recipientName}</span>
                  {!isSupport && isFriend && (
                    <Star size={14} className="text-amber-400 fill-amber-400 shrink-0" title="Amigo Verificado" />
                  )}
                </h3>
                {isSupport ? (
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-white/40">Soporte Técnico Oficial</p>
                  </div>
                ) : recipientPhone ? (
                  <a 
                    href={`https://wa.me/${recipientPhone.replace(/\+/g, '').replace(/\s/g, '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs text-white/50 hover:text-green-400 flex items-center gap-1 transition-colors group w-fit"
                  >
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current text-green-500 group-hover:scale-110 transition-transform">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                    </svg>
                    {recipientPhone}
                  </a>
                ) : (
                  <p className="text-xs text-white/50">Mensaje Privado</p>
                )}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>

          {/* Messages */}
          <div 
            ref={containerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-black/10"
          >
            {allMessages.length > 0 && (
              hasMore ? (
                <div className="text-center py-2 text-[10px] uppercase tracking-widest text-white/30 font-bold animate-pulse">
                  ↾ Desliza hacia arriba para ver mensajes anteriores ↾
                </div>
              ) : (
                <div className="text-center py-2 text-[10px] uppercase tracking-widest text-white/20 font-bold">
                  ✨ Inicio de la conversación — No hay más mensajes anteriores
                </div>
              )
            )}
            {allMessages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-white/30 text-sm space-y-4">
                <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center">
                  <Send size={24} className="opacity-50" />
                </div>
                <p>Envía un mensaje para iniciar la conversación</p>
              </div>
            ) : (
              allMessages.map((msg, index) => {
                const isMine = msg.senderId === currentUser.uid;
                const showTime = index === 0 || (msg.createdAt - allMessages[index-1].createdAt > 5 * 60 * 1000);
                
                return (
                  <div key={msg.id} className="flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-200">
                    {showTime && (
                      <div className="text-center text-[10px] text-white/30 my-2 uppercase tracking-widest font-bold">
                        {formatMessageDateTime(msg.createdAt)}
                      </div>
                    )}
                    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} group relative`}>
                      <div className={`relative max-w-[80%] flex ${isMine ? 'justify-end' : 'justify-start'} items-center gap-2`}>
                        <div 
                          className={`rounded-2xl p-3 text-sm font-medium shadow-md relative ${
                            isMine 
                              ? 'theme-primary-bg text-black rounded-br-sm' 
                              : 'bg-white text-black rounded-bl-sm'
                          } ${msg.isOptimistic ? 'opacity-60 animate-pulse' : 'opacity-100 transition-opacity'}`}
                        >
                          <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                          
                          {/* Render existing reaction */}
                          {msg.reaction && (
                            <div 
                              className={`absolute -bottom-3 ${isMine ? '-left-2' : '-right-2'} bg-black/80 rounded-full px-1.5 py-0.5 text-xs shadow-lg border border-white/20 z-10 ${!isMine && 'cursor-pointer hover:scale-110 transition-transform'}`}
                              onClick={() => !isMine && handleReaction(msg, msg.reaction!)}
                            >
                              {msg.reaction}
                            </div>
                          )}
                        </div>

                        {/* Reaction Menu Button (only for received messages) */}
                        {!isMine && (
                          <div className={`relative opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity ${activeReactionMsgId === msg.id ? 'opacity-100' : ''}`}>
                            <button
                              onClick={() => setActiveReactionMsgId(activeReactionMsgId === msg.id ? null : msg.id)}
                              className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/50 hover:text-white transition-colors"
                            >
                              <Smile size={16} />
                            </button>
                            
                            {/* Emoji Picker Popup */}
                            {activeReactionMsgId === msg.id && (
                              <div className="absolute top-1/2 -translate-y-1/2 left-8 bg-black/90 border border-white/10 rounded-full p-1 flex gap-1 shadow-xl z-[100]">
                                {REACTIONS.map(emoji => (
                                  <button
                                    key={emoji}
                                    onClick={() => handleReaction(msg, emoji)}
                                    className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full text-base transition-transform hover:scale-125"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {isSuspended && !isSupport ? (
            <div className="p-4 border-t border-red-500/20 bg-red-500/10 text-center text-red-400 font-bold text-xs uppercase tracking-wider py-5 px-6 flex items-center justify-center gap-2">
              <span>🚫 Cuenta inhabilitada. No puedes enviar mensajes privados.</span>
            </div>
          ) : (
            <form onSubmit={handleSendMessage} className="p-4 border-t border-white/5 bg-black/20">
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  className="w-full bg-black/40 border border-white/10 rounded-full py-3 pl-4 pr-12 text-sm text-white placeholder-white/30 focus:outline-none focus:border-theme-primary/50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="absolute right-2 p-2 theme-primary-bg rounded-full text-black hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100"
                >
                  <Send size={16} />
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>,
    document.body
  );
}
